import React from "react";
import styles from "./task-list-header.module.css";

export const TaskListHeaderDefault: React.FC<{
  headerHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  scheduleType: string;
  allSelected?: boolean;
  onSelectAll?: (selected: boolean) => void;
  columnWidths?: Record<string, number>;
  onColumnResize?: (colId: string, width: number) => void;
}> = ({
  headerHeight,
  fontFamily,
  fontSize,
  rowWidth,
  scheduleType,
  allSelected,
  onSelectAll,
  columnWidths,
  onColumnResize,
}) => {
  const widthOf = (colId: string, factor: number) =>
    columnWidths?.[colId] ?? parseInt(rowWidth) * factor;

  // Begin a column-resize drag. Mirrors the panel resizer in gantt.tsx:
  // record the start X + width, then update the column width on each mousemove.
  const startResize =
    (colId: string, startWidth: number) => (e: React.MouseEvent) => {
      if (!onColumnResize) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const onMove = (ev: MouseEvent) => {
        onColumnResize(colId, startWidth + (ev.clientX - startX));
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    };

  const cell = (
    colId: string,
    factor: number,
    content: React.ReactNode,
    opts: { title?: string; resizable?: boolean } = {}
  ) => {
    const { title, resizable = true } = opts;
    const width = widthOf(colId, factor);
    return (
      <div
        className={styles.ganttTable_HeaderItem}
        style={{ minWidth: width, maxWidth: width, position: "relative" }}
        title={title}
      >
        <div className={styles.ganttTable_HeaderItemText}>{content}</div>
        {resizable && onColumnResize && (
          <div
            className={styles.ganttTable_HeaderResizeHandle}
            style={{ height: headerHeight - 2 }}
            onMouseDown={startResize(colId, width)}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className={styles.ganttTable}
      style={{
        fontFamily: fontFamily,
        fontSize: fontSize,
      }}
    >
      <div
        className={styles.ganttTable_Header}
        style={{
          height: headerHeight - 2,
        }}
      >
        {onSelectAll &&
          cell(
            "select",
            0.3,
            <input
              type="checkbox"
              checked={allSelected}
              onChange={e => onSelectAll(e.target.checked)}
            />,
            { resizable: false }
          )}
        {cell("id", 0.8, "ID")}
        {cell("wbs", 0.8, "WBS Code / Activity ID")}
        {cell("name", 1.8, "Task")}
        {cell("plannedStart", 0.6, "Planned Start", { title: "Planned Start" })}
        {cell("plannedEnd", 0.6, "Planned End", { title: "Planned End" })}
        {scheduleType === "lookAhead" && (
          <React.Fragment>
            {cell("actualStart", 0.6, "Actual Start", { title: "Actual Start" })}
            {cell("actualEnd", 0.6, "Actual End", { title: "Actual End" })}
          </React.Fragment>
        )}
        {scheduleType === "main" && (
          <React.Fragment>
            {cell("percentComplete", 0.6, "% Complete", { title: "% Complete" })}
            {cell("plannedDuration", 0.6, "Planned Duration", {
              title: "Planned Duration",
            })}
            {cell("remainingDuration", 0.7, "Remaining Duration", {
              title: "Remaining Duration",
            })}
            {cell("actualDuration", 0.6, "Actual Duration", {
              title: "Actual Duration",
            })}
            {cell("durationType", 0.8, "Duration Type", {
              title: "Duration Type",
            })}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
